import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import type { AssignedFarmsContractStatusFilter, FarmFilterOptions } from '@/types/apiModels'
import { CONTRACT_STATUS_LABELS } from './contractStatusLabels'

export type FarmFiltersProps = {
  search: string
  onSearchChange: (value: string) => void
  programId: number | undefined
  onProgramIdChange: (programId: number | undefined) => void
  hubId: string | undefined
  onHubIdChange: (hubId: string | undefined) => void
  status: AssignedFarmsContractStatusFilter | undefined
  onStatusChange: (status: AssignedFarmsContractStatusFilter | undefined) => void
  onClear: () => void
  options: FarmFilterOptions
  optionsLoading?: boolean
  optionsDisabled?: boolean
}

const ALL_VALUE = ''

// Fixed on the frontend: not derived from filter-options, since the three contract statuses are
// not a farm attribute an admin adds to (card 5576).
const CONTRACT_STATUS_OPTIONS = Object.entries(CONTRACT_STATUS_LABELS).map(([value, label]) => ({
  value: value as AssignedFarmsContractStatusFilter,
  label,
}))

const FarmFilters = ({
  search,
  onSearchChange,
  programId,
  onProgramIdChange,
  hubId,
  onHubIdChange,
  status,
  onStatusChange,
  onClear,
  options,
  optionsLoading = false,
  optionsDisabled = false,
}: FarmFiltersProps) => {
  const selectsDisabled = optionsLoading || optionsDisabled

  // MUI warns on a value with no MenuItem (options still loading, or an out-of-scope deep link).
  const programValue = options.programs.some(program => program.id === programId)
    ? (programId as number)
    : ALL_VALUE
  const hubValue = options.hubs.some(hub => hub.id === hubId) ? (hubId as string) : ALL_VALUE

  const handleProgramChange = (event: SelectChangeEvent<number | typeof ALL_VALUE>) => {
    const { value } = event.target
    onProgramIdChange(value === ALL_VALUE ? undefined : Number(value))
  }

  const handleHubChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target
    onHubIdChange(value === ALL_VALUE ? undefined : value)
  }

  const handleStatusChange = (
    event: SelectChangeEvent<typeof ALL_VALUE | AssignedFarmsContractStatusFilter>,
  ) => {
    const { value } = event.target
    onStatusChange(value === ALL_VALUE ? undefined : (value as AssignedFarmsContractStatusFilter))
  }

  return (
    <Box sx={{ p: { xs: 2, sm: '22px' } }}>
      <TextField
        fullWidth
        label="Buscar establecimiento"
        placeholder="Buscar por nombre del establecimiento..."
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Limpiar búsqueda"
                  size="small"
                  onClick={() => onSearchChange('')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
        value={search}
        onChange={event => onSearchChange(event.target.value)}
      />

      <Box
        sx={{
          display: 'grid',
          // Mirrors the prototype's filter-row grid: 3 equal fields plus an auto-sized action,
          // collapsing to one column under the same 900px (md) breakpoint (card 5576).
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr auto' },
          columnGap: '18px',
          rowGap: 2,
          alignItems: { md: 'end' },
          mt: 2,
        }}
      >
        <FormControl fullWidth disabled={selectsDisabled}>
          <InputLabel id="farm-filters-program-label">Programa</InputLabel>
          <Select
            label="Programa"
            labelId="farm-filters-program-label"
            value={programValue}
            onChange={handleProgramChange}
          >
            <MenuItem value={ALL_VALUE}>Todos</MenuItem>
            {options.programs.map(program => (
              <MenuItem key={program.id} value={program.id}>
                {program.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={selectsDisabled}>
          <InputLabel id="farm-filters-hub-label">Nodo / Hub</InputLabel>
          <Select
            label="Nodo / Hub"
            labelId="farm-filters-hub-label"
            value={hubValue}
            onChange={handleHubChange}
          >
            <MenuItem value={ALL_VALUE}>Todos</MenuItem>
            {options.hubs.map(hub => (
              <MenuItem key={hub.id} value={hub.id}>
                {hub.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={selectsDisabled}>
          <InputLabel id="farm-filters-status-label">Estado contractual</InputLabel>
          <Select
            label="Estado contractual"
            labelId="farm-filters-status-label"
            value={status ?? ALL_VALUE}
            onChange={handleStatusChange}
          >
            <MenuItem value={ALL_VALUE}>Todos</MenuItem>
            {CONTRACT_STATUS_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          sx={{
            whiteSpace: 'nowrap',
            justifySelf: { xs: 'flex-start', md: 'auto' },
          }}
          onClick={onClear}
        >
          Limpiar filtros
        </Button>
      </Box>
    </Box>
  )
}

export default FarmFilters
